import React, { useState, useEffect } from 'react';
import { ArrowLeft, Beaker, Droplets, Trophy, HelpCircle, RotateCcw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GameMode, GameStats, Language } from '../types';
import useSound from 'use-sound';
import successSound from '../assets/sounds/success.wav';
import errorSound from '../assets/sounds/error2.mp3';

interface JuiceLabGameProps {
  mode: GameMode;
  onReset: () => void;
  language: Language;
}

interface Ingredient {
  name: string;
  color: string;
  fraction: { numerator: number; denominator: number };
}

interface Recipe {
  ingredients: Ingredient[];
  target: string;
  difficulty: number;
}

const JuiceLabGame: React.FC<JuiceLabGameProps> = ({ mode, onReset, language }) => {
  const [currentRecipe, setCurrentRecipe] = useState<Recipe | null>(null);
  const [beakerContents, setBeakerContents] = useState<Ingredient[]>([]);
  const [selectedIngredient, setSelectedIngredient] = useState<Ingredient | null>(null);
  const [pouringAmount, setPouringAmount] = useState('');
  const [challenge, setChallenge] = useState(1);
  const [score, setScore] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);
  const [showTutorial, setShowTutorial] = useState(true);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [stats, setStats] = useState<GameStats>({
    questionsAnswered: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    totalTime: 0,
    averageResponseTime: 0,
  });

  const [playSuccess] = useSound(successSound, { volume: 0.5 });
  const [playError] = useSound(errorSound, { volume: 0.3 });

  const availableIngredients = [
    { 
      name: language === 'en' ? 'Orange Juice' : 'Jus d\'orange', 
      color: '#F97316', 
      fraction: { numerator: 0, denominator: 1 } 
    },
    { 
      name: language === 'en' ? 'Apple Juice' : 'Jus de pomme', 
      color: '#EAB308', 
      fraction: { numerator: 0, denominator: 1 } 
    },
    { 
      name: language === 'en' ? 'Grape Juice' : 'Jus de raisin', 
      color: '#7C3AED', 
      fraction: { numerator: 0, denominator: 1 } 
    },
    { 
      name: language === 'en' ? 'Cranberry Juice' : 'Jus de canneberge', 
      color: '#DC2626', 
      fraction: { numerator: 0, denominator: 1 } 
    }
  ];

  const tutorialSteps = [
    {
      title: language === 'en' ? 'Welcome to the Juice Lab!' : 'Bienvenue au Laboratoire de Jus !',
      content: language === 'en' 
        ? 'Mix precise amounts of juice using fractions! Learn to measure and combine ingredients.'
        : 'Mélangez des quantités précises de jus en utilisant les fractions ! Apprenez à mesurer et combiner les ingrédients.'
    },
    {
      title: language === 'en' ? 'Understanding Measurements' : 'Comprendre les mesures',
      content: language === 'en'
        ? 'Each beaker holds 1 whole unit. 1/2 means half the beaker, 1/4 means a quarter.'
        : 'Chaque bécher contient 1 unité entière. 1/2 signifie la moitié du bécher, 1/4 signifie un quart.'
    },
    {
      title: language === 'en' ? 'Mixing Recipes' : 'Mélanger les recettes',
      content: language === 'en'
        ? 'Follow the recipe exactly! Select an ingredient, enter the fraction amount, and pour it in.'
        : 'Suivez la recette exactement ! Sélectionnez un ingrédient, entrez la quantité en fraction, et versez-le.'
    }
  ];

  const generateRecipe = (): Recipe => {
    const difficulty = Math.min(Math.floor((challenge - 1) / 5) + 1, 3);
    const numIngredients = difficulty === 1 ? 2 : difficulty === 2 ? 3 : 4;
    
    const denominators = difficulty === 1 ? [2, 4] : difficulty === 2 ? [2, 3, 4, 6] : [2, 3, 4, 5, 6, 8];
    const selectedIngredients = availableIngredients.slice(0, numIngredients);
    
    const recipe: Recipe = {
      ingredients: [],
      target: '',
      difficulty
    };
    
    // Generate fractions that add up to 1 or less
    let remainingAmount = 1;
    
    for (let i = 0; i < numIngredients; i++) {
      const isLast = i === numIngredients - 1;
      let fraction;
      
      if (isLast) {
        // Last ingredient gets the remaining amount
        const denominator = denominators[Math.floor(Math.random() * denominators.length)];
        const numerator = Math.round(remainingAmount * denominator);
        fraction = { numerator: Math.max(1, numerator), denominator };
      } else {
        const denominator = denominators[Math.floor(Math.random() * denominators.length)];
        const maxNumerator = Math.floor(remainingAmount * denominator);
        const numerator = Math.max(1, Math.floor(Math.random() * maxNumerator) + 1);
        fraction = { numerator, denominator };
        remainingAmount -= numerator / denominator;
      }
      
      recipe.ingredients.push({
        ...selectedIngredients[i],
        fraction
      });
    }
    
    recipe.target = language === 'en' 
      ? `Mix ${recipe.ingredients.map(ing => `${ing.fraction.numerator}/${ing.fraction.denominator} ${ing.name}`).join(', ')}`
      : `Mélangez ${recipe.ingredients.map(ing => `${ing.fraction.numerator}/${ing.fraction.denominator} ${ing.name}`).join(', ')}`;
    
    return recipe;
  };

  const addIngredient = () => {
    if (!selectedIngredient || !pouringAmount || showFeedback) return;
    
    try {
      const [numerator, denominator] = pouringAmount.split('/').map(Number);
      if (isNaN(numerator) || isNaN(denominator) || denominator === 0) return;
      
      const newIngredient = {
        ...selectedIngredient,
        fraction: { numerator, denominator }
      };
      
      setBeakerContents([...beakerContents, newIngredient]);
      setPouringAmount('');
      setSelectedIngredient(null);
    } catch (error) {
      console.error('Invalid fraction format');
    }
  };

  const checkRecipe = () => {
    if (!currentRecipe) return;
    
    let correct = true;
    
    // Check if we have the right number of ingredients
    if (beakerContents.length !== currentRecipe.ingredients.length) {
      correct = false;
    } else {
      // Check each ingredient
      for (const requiredIngredient of currentRecipe.ingredients) {
        const found = beakerContents.find(ingredient => 
          ingredient.name === requiredIngredient.name &&
          ingredient.fraction.numerator === requiredIngredient.fraction.numerator &&
          ingredient.fraction.denominator === requiredIngredient.fraction.denominator
        );
        
        if (!found) {
          correct = false;
          break;
        }
      }
    }
    
    setIsCorrect(correct);
    setShowFeedback(true);
    
    const newStats = {
      ...stats,
      questionsAnswered: stats.questionsAnswered + 1,
      correctAnswers: stats.correctAnswers + (correct ? 1 : 0),
      incorrectAnswers: stats.incorrectAnswers + (correct ? 0 : 1),
    };
    setStats(newStats);
    
    if (correct) {
      setScore(score + (currentRecipe.difficulty * 10));
      playSuccess();
    } else {
      playError();
    }
    
    setTimeout(() => {
      setChallenge(challenge + 1);
      if (challenge >= 15) {
        setGameComplete(true);
        saveProgress(newStats);
      } else {
        nextChallenge();
      }
    }, 2000);
  };

  const nextChallenge = () => {
    setCurrentRecipe(generateRecipe());
    setBeakerContents([]);
    setSelectedIngredient(null);
    setPouringAmount('');
    setShowFeedback(false);
  };

  const saveProgress = async (currentStats: GameStats) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from('user_progress').insert({
        user_id: user.id,
        operation: 'fractions',
        mode: 'juice',
        questions_answered: currentStats.questionsAnswered,
        correct_answers: currentStats.correctAnswers,
        average_response_time: 0,
      });
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const resetGame = () => {
    setChallenge(1);
    setScore(0);
    setGameComplete(false);
    setShowTutorial(true);
    setTutorialStep(0);
    setStats({
      questionsAnswered: 0,
      correctAnswers: 0,
      incorrectAnswers: 0,
      totalTime: 0,
      averageResponseTime: 0,
    });
    nextChallenge();
  };

  const clearBeaker = () => {
    setBeakerContents([]);
  };

  useEffect(() => {
    if (!showTutorial) {
      nextChallenge();
    }
  }, [showTutorial]);

  const calculateBeakerFill = () => {
    return beakerContents.reduce((total, ingredient) => {
      return total + (ingredient.fraction.numerator / ingredient.fraction.denominator);
    }, 0);
  };

  if (showTutorial) {
    return (
      <div className="w-full max-w-4xl">
        <div className="mb-6 flex items-center">
          <button
            onClick={onReset}
            className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
          </button>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <Beaker className="h-16 w-16 text-blue-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {tutorialSteps[tutorialStep].title}
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 mb-8">
            {tutorialSteps[tutorialStep].content}
          </p>
          
          <div className="flex justify-center gap-4">
            {tutorialStep > 0 && (
              <button
                onClick={() => setTutorialStep(tutorialStep - 1)}
                className="px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700"
              >
                {language === 'en' ? 'Previous' : 'Précédent'}
              </button>
            )}
            
            {tutorialStep < tutorialSteps.length - 1 ? (
              <button
                onClick={() => setTutorialStep(tutorialStep + 1)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {language === 'en' ? 'Next' : 'Suivant'}
              </button>
            ) : (
              <button
                onClick={() => setShowTutorial(false)}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {language === 'en' ? 'Start Mixing!' : 'Commencer à mélanger !'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl">
      <div className="mb-6 flex items-center justify-between">
        <button
          onClick={onReset}
          className="flex items-center text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
        >
          <ArrowLeft className="h-5 w-5 mr-1" />
          {language === 'en' ? 'Back to Modes' : 'Retour aux modes'}
        </button>
        
        <div className="flex items-center gap-4">
          <div className="text-lg font-medium text-blue-700 dark:text-blue-300">
            {language === 'en' ? `Challenge ${challenge}/15` : `Défi ${challenge}/15`}
          </div>
          
          <button
            onClick={() => setShowTutorial(true)}
            className="p-2 text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400"
          >
            <HelpCircle className="h-5 w-5" />
          </button>
        </div>
      </div>

      {gameComplete ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-8 text-center">
          <Trophy className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">
            {language === 'en' ? 'Master Chemist!' : 'Maître Chimiste !'}
          </h2>
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">{score}</div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Total Points' : 'Points totaux'}
              </div>
            </div>
            <div className="bg-slate-50 dark:bg-slate-700 p-4 rounded-lg">
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {Math.round((stats.correctAnswers / stats.questionsAnswered) * 100)}%
              </div>
              <div className="text-slate-600 dark:text-slate-300">
                {language === 'en' ? 'Accuracy' : 'Précision'}
              </div>
            </div>
          </div>
          <button
            onClick={resetGame}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {language === 'en' ? 'Play Again' : 'Rejouer'}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recipe Panel */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <Droplets className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                {language === 'en' ? 'Recipe' : 'Recette'}
              </h3>
            </div>
            
            {currentRecipe && (
              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg">
                  <p className="text-blue-800 dark:text-blue-200 font-medium mb-3">
                    {currentRecipe.target}
                  </p>
                  
                  <div className="space-y-2">
                    {currentRecipe.ingredients.map((ingredient, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: ingredient.color }}
                          ></div>
                          <span className="text-sm text-blue-700 dark:text-blue-300">
                            {ingredient.name}
                          </span>
                        </div>
                        <span className="font-mono text-blue-800 dark:text-blue-200">
                          {ingredient.fraction.numerator}/{ingredient.fraction.denominator}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  {language === 'en' ? 'Difficulty:' : 'Difficulté :'} 
                  <span className="ml-2">
                    {'★'.repeat(currentRecipe.difficulty)}{'☆'.repeat(3 - currentRecipe.difficulty)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Mixing Station */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                {language === 'en' ? 'Mixing Station' : 'Station de mélange'}
              </h3>
              <button
                onClick={clearBeaker}
                className="p-2 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400"
              >
                <RotateCcw className="h-4 w-4" />
              </button>
            </div>
            
            {/* Ingredient Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {language === 'en' ? 'Select Ingredient:' : 'Sélectionner un ingrédient :'}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {availableIngredients.map((ingredient, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedIngredient(ingredient)}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      selectedIngredient?.name === ingredient.name
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/50'
                        : 'border-slate-200 dark:border-slate-700 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: ingredient.color }}
                      ></div>
                      <span className="text-xs text-slate-700 dark:text-slate-300">
                        {ingredient.name}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Amount Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                {language === 'en' ? 'Amount (fraction):' : 'Quantité (fraction) :'}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={pouringAmount}
                  onChange={(e) => setPouringAmount(e.target.value)}
                  placeholder="1/2"
                  className="flex-1 p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                />
                <button
                  onClick={addIngredient}
                  disabled={!selectedIngredient || !pouringAmount || showFeedback}
                  className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {language === 'en' ? 'Pour' : 'Verser'}
                </button>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {language === 'en' ? 'Format: 1/2, 3/4, etc.' : 'Format : 1/2, 3/4, etc.'}
              </p>
            </div>
            
            <button
              onClick={checkRecipe}
              disabled={beakerContents.length === 0 || showFeedback}
              className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {language === 'en' ? 'Test Recipe' : 'Tester la recette'}
            </button>
          </div>

          {/* Beaker Visualization */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-6">
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4">
              {language === 'en' ? 'Your Mix' : 'Votre mélange'}
            </h3>
            
            {/* Beaker */}
            <div className="relative mx-auto w-32 h-48 border-4 border-slate-400 dark:border-slate-500 rounded-b-full bg-slate-50 dark:bg-slate-700 overflow-hidden">
              {/* Liquid layers */}
              {beakerContents.map((ingredient, index) => {
                const previousHeight = beakerContents.slice(0, index).reduce((sum, ing) => 
                  sum + (ing.fraction.numerator / ing.fraction.denominator), 0
                );
                const height = (ingredient.fraction.numerator / ingredient.fraction.denominator) * 100;
                
                return (
                  <div
                    key={index}
                    className="absolute bottom-0 left-0 right-0 transition-all duration-500"
                    style={{
                      backgroundColor: ingredient.color,
                      height: `${height}%`,
                      bottom: `${previousHeight * 100}%`,
                      opacity: 0.8
                    }}
                  />
                );
              })}
              
              {/* Fill level indicator */}
              <div className="absolute right-2 top-2 bottom-2 w-1 bg-slate-300 dark:bg-slate-600 rounded">
                <div 
                  className="absolute bottom-0 w-full bg-blue-500 rounded transition-all duration-500"
                  style={{ height: `${Math.min(calculateBeakerFill() * 100, 100)}%` }}
                />
              </div>
            </div>
            
            {/* Contents List */}
            <div className="mt-4 space-y-2">
              {beakerContents.map((ingredient, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: ingredient.color }}
                    ></div>
                    <span className="text-slate-700 dark:text-slate-300">
                      {ingredient.name}
                    </span>
                  </div>
                  <span className="font-mono text-slate-600 dark:text-slate-400">
                    {ingredient.fraction.numerator}/{ingredient.fraction.denominator}
                  </span>
                </div>
              ))}
            </div>
            
            <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600 dark:text-slate-400">
                  {language === 'en' ? 'Total:' : 'Total :'}
                </span>
                <span className="font-mono text-slate-800 dark:text-slate-200">
                  {calculateBeakerFill().toFixed(2)}
                </span>
              </div>
            </div>

            {showFeedback && (
              <div className={`mt-4 p-3 rounded-lg ${
                isCorrect 
                  ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                  : 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
              }`}>
                <p className="font-medium text-center">
                  {isCorrect 
                    ? (language === 'en' ? 'Perfect Recipe!' : 'Recette parfaite !')
                    : (language === 'en' ? 'Try Again!' : 'Réessayez !')}
                </p>
              </div>
            )}
            
            <div className="mt-4 text-center text-sm text-slate-500 dark:text-slate-400">
              {language === 'en' ? 'Score:' : 'Score :'} {score}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JuiceLabGame;